/**
 * The subject an envelope gets when a FILE names it, and the bound every subject is held to.
 *
 * Split out of `workflow/index.ts` — which re-exports both, so nothing that imports them from
 * `@flowza/core` changes — for a measured reason. The console's Envelopes card now offers "Upload a
 * document" beside "Write a subject", and it needs exactly these two values; importing them through
 * `@flowza/core/workflow` put the EDITOR route at 180 KB against a 150 KB budget derived from the
 * p75 signing-TTI target, because that module sits in a chunk the editor would then have to fetch.
 * A leaf with no imports of its own costs the same wherever it is named.
 *
 * The prefix itself stays in `workflow/index.ts`: it is a property of the International standard,
 * and the reasons it is a constant rather than a tenant setting are argued there.
 */
import { INTERNATIONAL_SUBJECT_PREFIX } from './subject-prefix'

export { INTERNATIONAL_SUBJECT_PREFIX }

/** The longest a subject may be — `public.create_envelope`'s own bound. */
export const SUBJECT_MAX = 200

/**
 * The longest a MESSAGE to signers may be, and the one place that number is now written.
 *
 * `apps/api/src/routes/envelopes.ts` refuses 2001 characters with `message_too_long`. The composer's
 * Set-up step counted to **10,000** and showed "0/10000" under the box, and the Review-and-send
 * step's textarea took 2,000 — three numbers for one bound, two of them wrong. A message between
 * 2,001 and 10,000 characters could therefore be typed in step 1, was refused by the API on send,
 * and could not be shortened in step 3 because that box would not hold it (the testing team,
 * 2026-09-16, BUG-13).
 *
 * Here rather than in each file for the reason `SUBJECT_MAX` is here: a leaf with no imports of its
 * own costs nothing wherever it is named, and a bound stated three times is a bound that drifts.
 */
export const MESSAGE_MAX = 2000

/**
 * The subject for an envelope whose first document is named `title`.
 *
 * `Complete with FlowZa Sign: lease.pdf` — the FILENAME with its extension, because that is what a
 * recipient recognises from the attachment, and it is DocuSign's own behaviour (founder's
 * instruction, 2026-09-13).
 *
 * This comment used to say "the title keeps its extension", and it did not: `titleFromFilename`
 * strips the extension and re-cases what is left, and the caller passed THAT, so the envelope was
 * named "E Itinerary" while this paragraph promised "E-itinerary.pdf". The function was never the
 * problem — its own unit test passes `lease.pdf` and expects `lease.pdf` back — the input was. The
 * caller (`followFirstDocumentTitle`) now passes `document_versions.original_filename` and falls
 * back to the title only for a document with no version yet.
 */
export function generatedSubject(title: string): string {
  const cleaned = title.replace(/\s+/g, ' ').trim()
  const subject = `${INTERNATIONAL_SUBJECT_PREFIX}${cleaned === '' ? '(untitled)' : cleaned}`
  // Truncated rather than refused: a filename is not something a sender chose with a subject line
  // in mind, and refusing a send because somebody's file has a long name would be absurd. The
  // ellipsis is one character rather than three dots, so the length is what it looks like.
  return subject.length <= SUBJECT_MAX ? subject : `${subject.slice(0, SUBJECT_MAX - 1)}…`
}
